---
title: "Transferring BookBurrow to a new computer"
slug: "transferring-to-a-new-computer"
category: "admin"
description: "Move your library — data, settings, and licence — from one machine to another without missing a beat."
tags: ["transfer", "migrate", "new computer", "replacement", "licence"]
order: 3
---

At some point every library computer gets replaced. Maybe it's died, maybe IT is rolling out new Windows machines, maybe the library has moved rooms. The good news: moving BookBurrow to a new computer is a well-trodden path and takes about fifteen minutes if your backups are in good shape.

## What you'll move

- The **live database** (or the latest backup, which is equivalent).
- The **licence activation** (one deactivate, one activate).
- Any **custom settings**: backup folder, PINs, branding, templates.

## Before you start

Make sure you have:

- A recent backup you can find. If BookBurrow's automatic backups have been running to OneDrive or a network share, you're set. If not, **run Settings → Backups → Backup now** on the old computer first.
- Your **licence key** (`BB-XXXX-XXXX-XXXX-XXXX`). Check the email we sent at purchase time, or copy it from **Settings → Licence** on the old computer.
- Access to both computers, or the ability to copy a file between them.

## Step 1 — Deactivate on the old computer

<Steps>
  <Step title="Open BookBurrow on the old computer">
    Enter the admin PIN.
  </Step>
  <Step title="Go to Settings → Licence">
    Click **Deactivate this computer**. This tells our licence server that this machine is no longer using the licence.
  </Step>
  <Step title="Confirm">
    The licence status changes to "Not activated" and one activation slot is freed up.
  </Step>
</Steps>

<Callout variant="warning" title="If the old computer won't turn on">
If the old machine has died and you can't deactivate it, don't panic — email support@bookburrow.uk with your licence key and we'll free up the activation manually within a few working hours.
</Callout>

## Step 2 — Install BookBurrow on the new computer

Follow the normal [installing BookBurrow](/help/guides/installing-bookburrow) guide on the new machine. Stop before restoring any data — we'll do that in the next step.

## Step 3 — Restore your data

<Steps>
  <Step title="Find your latest backup">
    If backups were going to OneDrive, install OneDrive on the new machine and let it sync. The backup folder will appear with all your `.bbbackup` files inside.
  </Step>
  <Step title="Open BookBurrow on the new computer">
    On first run, BookBurrow offers to **Restore from an existing backup** — perfect.
  </Step>
  <Step title="Pick the most recent backup file">
    BookBurrow shows the timestamps of all the backups it finds. Pick the newest one.
  </Step>
  <Step title="Wait for the restore">
    For a typical primary school library, this takes under a minute.
  </Step>
</Steps>

When the restore finishes, BookBurrow opens with everything back in place: borrowers, books, loan history, settings, PINs, branding.

## Step 4 — Activate the licence

<Steps>
  <Step title="Open Settings → Licence">
    Enter your licence key exactly as it appears in the email.
  </Step>
  <Step title="Click Activate">
    Because you deactivated on the old machine, there's a free slot and activation succeeds in a second.
  </Step>
</Steps>

See [Activating your licence](/help/guides/activating-your-licence) if the activation step fails.

## Step 5 — Sanity check

Before putting the new computer into production, do a quick five-point check:

- Open the **Borrowers** tab — does your pupil list look right?
- Open the **Books** tab — does your catalogue count match what you remember?
- Lend a test book to a test borrower — does the loan go through?
- Return it — does the book become available again?
- Check **Settings → Backups** — is the backup folder set (you may need to re-point it on the new computer)?

If all five pass, you're done. The old computer is now safe to reset or dispose of.

## Keeping both computers running temporarily

If you want to run BookBurrow on both machines for a short overlap (say, a week while you're phasing out the old one), contact support@bookburrow.uk and we can issue a temporary second activation for free. Longer-term multi-machine use requires a multi-seat licence.
